"use client";

import React from "react";
import { Loader, Send, Info, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={`flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Checkbox = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input type="checkbox" className={`peer h-5 w-5 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 accent-[hsl(188,86%,53%)] cursor-pointer ${className}`} ref={ref} {...props} />;
});
Checkbox.displayName = "Checkbox";
function MythSubmissionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="rounded-full bg-teal-500/10 p-4 ring-1 ring-teal-500/20">
          <CircleCheck className="size-12 text-teal-400" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-medium text-foreground">
            Submission Received
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {successMessage || "Thank you for contributing to our research. We will investigate this myth shortly."}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-teal-500/30 hover:bg-teal-500/10 hover:text-teal-300">
          Submit Another Myth
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: false
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} value={field.value || ""} />
              </FormControl>
              <FormDescription>Optional – we may use this to thank you.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} value={field.value || ""} />
              </FormControl>
              <FormDescription>We'll use this to follow up if needed.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="myth_title" rules={{
      required: "Myth title is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Myth Title</FormLabel>
            <FormControl>
              <Input placeholder="e.g., “Brushing hard cleans better”" {...field} value={field.value || ""} />
            </FormControl>
            <FormDescription>A short, clear title for the myth.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="myth_description" rules={{
      required: "Description is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Myth Description</FormLabel>
            <FormControl>
              <Textarea placeholder="Explain the myth, where you heard it, and why you think it might be false." className="min-h-[120px] bg-background/50" {...field} value={field.value || ""} />
            </FormControl>
            <FormDescription>Provide as much detail as possible.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="source" rules={{
      required: false,
      pattern: {
        value: /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/[a-zA-Z0-9]+\.[^\s]{2,})$/,
        message: "Please enter a valid URL"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Source (optional)</FormLabel>
            <FormControl>
              <Input type="url" placeholder="https://example.com/article" {...field} value={field.value || ""} />
            </FormControl>
            <FormDescription>Link to where you saw the myth.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="agree_terms" rules={{
      required: "You must confirm the information is accurate."
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-input bg-background/30 p-4">
            <FormControl>
              <Checkbox checked={field.value} onChange={field.onChange} className="mt-1" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal text-foreground/90 cursor-pointer">
                I confirm that the information provided is accurate to the best of my knowledge.
              </FormLabel>
              <FormDescription>Required to submit the myth.</FormDescription>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3 text-red-400">
          <CircleAlert className="size-5 shrink-0" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-[0_0_20px_rgba(6,182,212,0.3)] border-none h-12 text-base font-medium transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting...
          </> : <>
            Submit for Investigation
            <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function SubmitAMyth() {
  return <section id="submit-a-myth" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full opacity-20 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-cyan-500/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto max-w-3xl relative z-10">
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center justify-center p-2 rounded-full bg-secondary/30 backdrop-blur-sm border border-white/5 mb-4">
            <Info className="size-4 text-cyan-400 mr-2" />
            <span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">
              Help Us Clarify The Truth
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight text-foreground">
            Submit a Myth
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Heard something about dental health that sounds suspicious? Share it with us. 
            Our team of experts will investigate and publish the scientific reality.
          </p>
        </div>

        <Card className="bg-slate-900 border-slate-800 shadow-2xl overflow-hidden">
          <CardHeader className="border-b border-slate-800/50 pb-8 bg-slate-900/50">
            <CardTitle className="text-xl font-medium text-white">
              Submission Form
            </CardTitle>
            <CardDescription className="text-slate-400">
              Please provide as much detail as possible to help our research team.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8 bg-slate-900">
            <FormProvider formId="myth_submission_form_dental_myths" sectionName="submit-a-myth" defaultValues={{
            name: "",
            email: "",
            myth_title: "",
            myth_description: "",
            source: "",
            agree_terms: false
          }} validationRules={{
            name: {
              required: false
            },
            email: {
              required: true,
              pattern: "email"
            },
            myth_title: {
              required: true
            },
            myth_description: {
              required: true
            },
            source: {
              required: false,
              pattern: "url"
            },
            agree_terms: {
              required: true
            }
          }} submitText="Submit for Investigation" successMessage="Thank you! Your myth has been submitted for review.">
              <MythSubmissionForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}